import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Typography, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, LinkColumn, StandardDialog } from '@roc/ui';
import { CadenceStepsComponent } from "./cadenceStepsComponent";
import { useUserStore } from "@roc/feature-app-core";
import { Roles } from "@roc/feature-utils";
import MetricPersonalInfromation from "./metricsPersonalInformation";
import { useLeadDetailsRoutes } from "../../routes/loanRoutes";
import { useBorrowerDetailsRoutes } from "../../routes/borrowerDetailsRoutes";
import { useTPOContactDetailsRoutes } from "../../routes/tpoContactDetailsRoutes";
import { Link } from "react-router-dom";
import { useStore } from "@roc/client-portal-shared/hooks";

const useStyles = makeStyles(theme => ({
  containerDetails: {
    display: "flex",
    justifyContent: "flex-start",
    backgroundColor: "#FBFBFB",
    border: "#FBFBFB 1px solid",
    padding: "20px",
    overflowX: "auto",
    gap: "24px",
    scrollbarWidth: "thin",
  },
  containerCadenceInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "10px",
  },
  stepsContainer: {
    minHeight: "400px",
    maxHeight: "500px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "10px",
    padding: "10px",
    backgroundColor: "white",
    borderRadius: "8px",
  },
}));

const metricLabelsForBorrowers = {
  steps: "Steps",
  duration: "Duration (Days)",
  totalProspects: "Total Prospects",
  activeProspects: "Active Prospects",
  emailsSent: "Emails Sent",
  emailClicks: "Email Clicks",
  emailOpens: "Email Opens",
  textSent: "Text Sent",
  callsDialed: "Calls Dialed",
  totalLoans: "Total Loans",
  totalBgChecksRun: "Credit/BG Checks",
  totalSmsOptOuts: "SMS Opt-Outs",
  totalEmailOptOuts: "Email Opt-Outs",
};

const metricLabels = {
  steps: "Steps",
  duration: "Duration (Days)",
  totalProspects: "Total Prospects",
  activeProspects: "Active Prospects",
  emailsSent: "Emails Sent",
  emailClicks: "Email Clicks",
  emailOpens: "Email Opens",
  textSent: "Text Sent",
  callsDialed: "Calls Dialed",
  totalSmsOptOuts: "SMS Opt-Outs",
  totalEmailOptOuts: "Email Opt-Outs",
};

const clickableLabels = ["Credit/BG Checks", "Email Opens", "Email Clicks", "Total Loans"];

export const OverviewCadenceDetailsTab = observer(({ cadence, handleOpenModal }) => {
  const { userStore } = useUserStore();
  const [open, setOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const isBackOfficeLead = userStore.userInformation?.roles?.includes(Roles.BACK_OFFICE_LEAD);
  const classes = useStyles();
  const recordType = cadence?.recordType?.toLowerCase?.() ?? '';
  const metrics = recordType === 'borrower' ? metricLabelsForBorrowers : metricLabels;

  const getMetricPersonalInfo = (label) => {
    switch (label) {
      case "Email Opens":
        return cadence?.metrics?.emailOpensPersonalInfo;
      case "Email Clicks":
        return cadence?.metrics?.emailClicksPersonalInfo;
      case "Total Loans":
        return cadence?.metrics?.totalLoansPersonalInfo;
      case "Credit/BG Checks":
        return cadence?.metrics?.totalBgChecksRunPersonalInfo;
      default:
        return [];
    }
  }

  const showTotalColumn = (label) => {
    switch (label) {
      case "Email Opens":
        return false
      case "Email Clicks":
        return false
      case "Total Loans":
        return true;
      case "Credit/BG Checks":
        return true;
      default:
        return false;
    }
  }

  const getEventDateColLabel = (label) => {
    switch (label) {
      case "Email Opens":
        return "Email Open Date"
      case "Email Clicks":
        return "Email Click Date"
    }
  }

  const [lenderByCompany, setLenderByCompany] = useState<any>(null);
  const { cadenceStore, salesforceLenderStore, salesforceLeadStore } = useStore();

  useEffect(() => {
    cadenceStore.fetchCadencesDueByBackofficeOriginatorAndCadenceId();
  }, [cadenceStore]);

  useEffect(() => {
    (async () => {
      try {
        const user = await userStore.getUserInformation();
        const companyId = user?.user?.salesSupportCompanyId;
        if (!companyId) return;

        const lender = await salesforceLenderStore.fetchLenderByCompanyId(
          companyId
        );
        setLenderByCompany(
          lender ?? (salesforceLenderStore as any)?.lender ?? null
        );
      } catch (e) {
        salesforceLeadStore.globalStore.notificationStore.showErrorNotification(
          {
            message: 'Unable to load lender for current user',
          }
        );
        console.error(e);
      }
    })();
  }, []);

  const handleRefreshCadenceData = () => {
    cadenceStore.fetchCadencesDueByBackofficeOriginatorAndCadenceId();
    cadenceStore.fetchCadenceContentDataByCadenceId(cadence.cadenceId)
  };

  return (
    <>
      <div className={classes.containerDetails}>
        {Object.entries(cadence.metrics)
          .filter(([key]) => metrics.hasOwnProperty(key))
          .map(([key, value]) => {
            const label = metrics[key];
            const onClick = clickableLabels.includes(label)
              ? () => {
                setSelectedMetric(label);
                setOpen(true);
              }
              : undefined;
            return (
              <div
                key={key}
                className={classes.containerCadenceInfo}
                onClick={onClick}
                style={{ cursor: onClick ? "pointer" : "default" }}
              >
                <Typography variant="body2">{label}</Typography>
                <Typography variant="h6" style={{ textDecoration: onClick ? "underline" : "none" }}>
                  {value}
                </Typography>
              </div>
            );
          })}
      </div>

      {isBackOfficeLead && (
        <div className={classes.buttonContainer}>
          <Button
            testId="addStep"
            variant="contained"
            color="primary"
            style={{ margin: "10px 0" }}
            onClick={handleOpenModal}
          >
            + Add Step
          </Button>
        </div>
      )}

      <div className={classes.stepsContainer}>
        {cadence.steps.length > 0 ? (
          cadence.steps.map((step, index) => (
            <div key={index} style={{ width: "100%" }}>
              <CadenceStepsComponent step={step} index={index} shouldRenderActions={isBackOfficeLead} lenderByCompany={lenderByCompany} onClose={handleRefreshCadenceData} />
            </div>
          ))
        ) : (
          <Typography variant="h6" align="center" style={{ color: "#AAA", marginTop: "20px" }}>
            No steps available
          </Typography>
        )}
      </div>

      <StandardDialog
        open={open}
        title={`Details for ${selectedMetric}`}
        handleClose={() => setOpen(false)}
        maxWidth="lg"
        dialogContent={
          <MetricPersonalInfromation records={getMetricPersonalInfo(selectedMetric)} showTotalColumn={showTotalColumn(selectedMetric)} eventDateColLabel={getEventDateColLabel(selectedMetric)}/>
        }
        dialogActions={
          <Button testId={"close"} onClick={() => setOpen(false)} color="default" style={{ marginRight: '10px' }}>
            Cancel
          </Button>
        }
      />
    </>
  );
});
